"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Rateleakageanalysis() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "OTA Rate Parity Monitoring",
    description: "Real-time detection of unauthorized rate discrepancies across global OTAs to protect your direct booking margins.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Competitor Pricing Intelligence",
    description: "Granular analysis of competitor pricing shifts, revealing the logic behind their market positioning and demand capture.",
    Icon: Brain
  }, {
    number: "03",
    title: "Revenue Leakage Audits",
    description: "Continuous monitoring of booking channels to identify hidden revenue loss and optimize your distribution strategy.",
    Icon: Radar
  }, {
    number: "04",
    title: "Market Demand Heatmapping",
    description: "Visualized booking trends and demand curves across your region, identifying peak opportunities and undervalued segments.",
    Icon: Compass
  }, {
    number: "05",
    title: "Guest Sentiment Modeling",
    description: "Multi-source sentiment synthesis from reviews and social channels, weighted by influence to improve your property reputation.",
    Icon: Activity
  }, {
    number: "06",
    title: "Predictive Revenue Forecasting",
    description: "Forward-looking revenue modeling tuned to historical booking patterns, seasonal windows, and local market behavior.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="rateleakageanalysis" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs tracking-widest uppercase text-secondary mb-6">
              03 / Revenue Solutions
            </p>
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Revenue intelligence, optimized.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">
              Our methodology fuses data-driven revenue management with hands-on hospitality expertise, transforming raw booking data into actionable profit growth. Each solution operates as a coordinated layer within your hotel's revenue architecture.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-background border border-transparent hover:border-secondary/40 transition-all duration-500 ease-out p-8 sm:p-10 flex flex-col shadow-md rounded-lg" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                {}
                <span className="font-default text-xs tracking-widest text-secondary">
                  {cap.number}
                </span>

                {}
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-secondary/15 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-8 text-foreground/90 group-hover:text-secondary transition-colors duration-500" />
                </div>

                {}
                <h3 className="font-default font-semibold text-2xl leading-snug text-foreground mb-4">
                  {cap.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground/80 mb-8">
                  {cap.description}
                </p>

                {}
                <div className="mt-auto">
                  <Link to="/OurServicesPage" className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-foreground/80 hover:text-secondary transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-secondary to-secondary bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        Learn More
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-sm text-muted-foreground/80 max-w-xl">
            Maximize your property's revenue potential with HotelRevBaBa's expert consulting services.
          </p>
          <Button asChild size="lg" className="btn-primary font-default text-xs tracking-widest uppercase">
            <Link to="/ContactPage">Book a Consultation</Link>
          </Button>
        </div>
      </div>
    </section>;
}